<script setup lang="ts">
import type { Api } from '@rialtic/api'
import { has, get } from 'json-pointer'
import { format as formatDate, parseISO } from 'date-fns'
import type { RowSchema } from '../utils/claim-editor'
import { resolveWidget } from '../utils/claim-widgets'

interface FhirFieldSchema {
  label: string
  path: string
  cellClass?: string
  format?: (value: string) => string
  testId: string
}

const props = defineProps<{
  fields: RowSchema[]
  fhirClaim: Api.ProcessedClaimWithInsights | undefined
}>()

const getFhirField = (field: FhirFieldSchema): string => {
  if (props.fhirClaim && has(props.fhirClaim, field.path)) {
    const value = get(props.fhirClaim, field.path)

    return field.format?.(value) || value
  }

  return '-'
}

const readonlyFhirFields: FhirFieldSchema[] = [
  {
    label: 'Patient Name',
    path: '/patientName',
    testId: 'patient_name',
  },
  {
    label: 'Member ID',
    path: '/subscriberId',
    testId: 'member_id',
  },
  {
    label: 'Member Group ID',
    path: '/memberGroupId',
    testId: 'member_group_id',
  },
  {
    label: 'Patient Birthdate',
    path: '/patient/birthDate',
    format: (value) => formatDate(parseISO(value), 'MM/dd/yyyy'),
    testId: 'patient_birthdate',
  },
  {
    label: 'Patient Sex',
    path: '/patient/gender',
    testId: 'patient_sex',
    cellClass: 'capitalize',
  },
  {
    label: 'Prior Auth #',
    path: '/insurance/0/preAuthRef/0',
    testId: 'prior_auth',
  },
]
</script>

<template>
  <section class="w-full">
    <h2
      class="bg-primary-200 text-inverted z-1 sticky top-0 px-2 py-2 text-xs sm:px-4 lg:px-7"
    >
      Claim Header Fields
    </h2>

    <table
      v-if="fhirClaim && fields.length"
      class="w-full border-separate border-spacing-px"
    >
      <tbody>
        <tr v-for="(fhirField, i) in readonlyFhirFields" :key="i">
          <th
            class="bg-default outline-default w-[220px] px-2 py-2 text-left text-xs font-normal outline outline-1 sm:px-4 lg:px-7"
            scope="row"
          >
            {{ fhirField.label }}
          </th>

          <td
            :class="[
              fhirField.cellClass || '',
              'bg-default outline-default p-2 text-left text-xs font-normal outline outline-1',
            ]"
            :data-testid="`ClaimHeaderEditor__${fhirField.testId}`"
          >
            {{ getFhirField(fhirField) }}
          </td>
        </tr>

        <template v-for="field in fields" :key="field.name">
          <tr v-if="!field.isHidden">
            <th
              :id="`${field.editKey}-label`"
              class="bg-default outline-default w-[220px] px-2 py-2 text-left text-xs font-normal outline outline-1 sm:px-4 lg:px-7"
              scope="row"
            >
              {{ field.label }}
              <span v-if="field.isRequired" class="text-red-700">*</span>
            </th>
            <td
              v-if="field.isEditable"
              class="bg-elevated block w-full p-0 outline outline-dashed outline-1 outline-neutral-500"
            >
              <component
                :is="resolveWidget(field.component!)"
                :name="field.name"
                :attributes="{
                  autocomplete: 'off',
                  'data-1p-ignore': true,
                  'data-lpignore': true,
                  'data-no-autofill': true,
                  placeholder: field.placeholder,
                  required: field.isRequired,
                  readonly: field.isReadonly,
                  class: 'w-full bg-transparent',
                }"
                :label="field.label"
                :max-entries="field.maxEntries"
                :ref-data-model="field.refDataModel"
                :data-type="field.dataType || 'string'"
              />
            </td>
            <td
              v-else
              class="bg-default outline-default p-2 text-left text-xs font-normal outline outline-1"
            >
              {{ String(field.modelValue ?? '') || '-' }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </section>
</template>
